import { useEffect, useMemo, useState } from "react";
import { apiRequest } from "../api";

const DEFAULT_INVOICE_CONFIGURATION = {
    invoiceModule: "Both",
    emailNotifications: "N",
    vendorSPOCEmail: "",
    materialApprovalStage: "before_grn",
    serviceApprovalStage: "before_grn",
    typeOfInvoiceEnabled: "Y",
    typeAssignmentMode: "manual",
    automaticTypeChoice: "",
    checklistEnabled: "Y",
};

function parseChecklistItems(rawValue) {
    return rawValue
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
}

export default function ChecklistsView() {
    const [activeTab, setActiveTab] = useState("add_checklist");
    const [invoiceTypes, setInvoiceTypes] = useState([]);
    const [loadingInvoiceTypes, setLoadingInvoiceTypes] = useState(false);
    const [invoiceTypeError, setInvoiceTypeError] = useState("");

    const [checklists, setChecklists] = useState([]);
    const [loadingChecklists, setLoadingChecklists] = useState(false);
    const [selectedDocumentType, setSelectedDocumentType] = useState("");
    const [checklistItemsRaw, setChecklistItemsRaw] = useState("");
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");
    const [savingChecklist, setSavingChecklist] = useState(false);
    const [invoiceConfiguration, setInvoiceConfiguration] = useState(DEFAULT_INVOICE_CONFIGURATION);
    const [loadingChecklistConfiguration, setLoadingChecklistConfiguration] = useState(true);
    const [savingChecklistConfiguration, setSavingChecklistConfiguration] = useState(false);

    const [approverDocumentType, setApproverDocumentType] = useState("");
    const [approverSelections, setApproverSelections] = useState({});

    // Load invoice types on mount
    useEffect(() => {
        setLoadingInvoiceTypes(true);
        setInvoiceTypeError("");

        apiRequest("/invoice-types/")
            .then((response) => {
                const invoiceTypeRows = Array.isArray(response?.data) ? response.data : [];
                const names = invoiceTypeRows
                    .map((row) => (row?.name || "").trim())
                    .filter(Boolean);
                setInvoiceTypes(names);
            })
            .catch((requestError) => {
                setInvoiceTypeError(requestError.message || "Unable to load invoice types.");
            })
            .finally(() => {
                setLoadingInvoiceTypes(false);
            });
    }, []);

    // Load checklists on mount
    useEffect(() => {
        loadChecklists();
    }, []);

    useEffect(() => {
        if (!status) {
            return undefined;
        }

        const timer = window.setTimeout(() => setStatus(""), 3500);
        return () => window.clearTimeout(timer);
    }, [status]);

    useEffect(() => {
        let cancelled = false;

        apiRequest("/invoice-configuration/")
            .then((response) => {
                if (!cancelled && response?.status === "success" && response.data) {
                    setInvoiceConfiguration((current) => ({ ...current, ...response.data }));
                }
            })
            .catch((requestError) => {
                if (!cancelled) {
                    setError(requestError.message || "Unable to load checklist configuration.");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingChecklistConfiguration(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, []);

    function loadChecklists() {
        setLoadingChecklists(true);
        apiRequest("/checklists/")
            .then((response) => {
                const checklistData = Array.isArray(response?.data) ? response.data : [];
                setChecklists(checklistData);
            })
            .catch((requestError) => {
                console.error("Error loading checklists:", requestError);
                setChecklists([]);
            })
            .finally(() => {
                setLoadingChecklists(false);
            });
    }

    const aggregatedRows = useMemo(() => checklists, [checklists]);
    const checklistsEnabled = invoiceConfiguration.checklistEnabled !== "N";

    async function handleChecklistEnabledChange(value) {
        const nextConfiguration = {
            ...invoiceConfiguration,
            checklistEnabled: value,
        };

        setInvoiceConfiguration(nextConfiguration);
        setSavingChecklistConfiguration(true);
        setError("");
        setStatus("");

        try {
            const response = await apiRequest("/invoice-configuration/save/", {
                method: "POST",
                body: nextConfiguration,
            });
            setInvoiceConfiguration((current) => ({ ...current, ...(response?.data || {}) }));
            setStatus(response?.message || "Checklist configuration updated.");
        } catch (requestError) {
            setInvoiceConfiguration(invoiceConfiguration);
            setError(requestError.message || "Unable to update checklist configuration.");
        } finally {
            setSavingChecklistConfiguration(false);
        }
    }

    // Get items for selected approver document type
    const approverRows = useMemo(() => {
        const checklist = checklists.find((c) => c.invoice_type_name === approverDocumentType);
        if (!checklist || !Array.isArray(checklist.items)) {
            return [];
        }
        return checklist.items.map((item, index) => ({
            id: `${checklist.id}-${index}`,
            checklistItem: item,
        }));
    }, [checklists, approverDocumentType]);

    function loadApproverSelectionsForType(documentType) {
        if (!documentType) {
            setApproverSelections({});
            return;
        }

        const checklist = checklists.find((c) => c.invoice_type_name === documentType);
        if (!checklist) {
            setApproverSelections({});
            return;
        }

        const invoiceTypeId = checklist.id;
        const checklistItems = Array.isArray(checklist.items) ? checklist.items : [];

        apiRequest(`/checklists/${invoiceTypeId}/`)
            .then((response) => {
                const savedSelections = response?.data?.approver_selections || {};
                const nextSelections = {};

                for (let approverNum = 1; approverNum <= 6; approverNum++) {
                    const approverKey = `approver${approverNum}`;
                    const selectedItems = Array.isArray(savedSelections[approverKey])
                        ? savedSelections[approverKey]
                        : [];

                    checklistItems.forEach((item, itemIndex) => {
                        const rowId = `${invoiceTypeId}-${itemIndex}`;
                        const checkboxKey = `${rowId}-approver-${approverNum}`;
                        nextSelections[checkboxKey] = selectedItems.includes(item);
                    });
                }

                setApproverSelections(nextSelections);
            })
            .catch(() => {
                setApproverSelections({});
            });
    }

    function handleSubmitChecklist(event) {
        event.preventDefault();

        if (!checklistsEnabled) {
            return;
        }

        const documentType = selectedDocumentType.trim();
        const parsedItems = parseChecklistItems(checklistItemsRaw);

        if (!documentType) {
            setError("Please select a document type.");
            setStatus("");
            return;
        }

        if (!parsedItems.length) {
            setError("Please enter at least one checklist item.");
            setStatus("");
            return;
        }

        setError("");
        setStatus("");
        setSavingChecklist(true);

        apiRequest("/checklists/", {
            method: "POST",
            body: {
                invoice_type_name: documentType,
                items: parsedItems,
            },
        })
            .then((response) => {
                setStatus(response?.message || "Checklist items saved successfully.");
                setChecklistItemsRaw("");
                setSelectedDocumentType("");
                loadChecklists();
            })
            .catch((requestError) => {
                setError(requestError.message || "Unable to save checklist.");
            })
            .finally(() => {
                setSavingChecklist(false);
            });
    }

    function getApproverSelectionValue(rowId, approverIndex) {
        return approverSelections[`${rowId}-approver-${approverIndex}`] || false;
    }

    function toggleApproverSelection(rowId, approverIndex) {
        setApproverSelections((current) => {
            const key = `${rowId}-approver-${approverIndex}`;
            return {
                ...current,
                [key]: !current[key],
            };
        });
    }

    function handleSubmitApproverChecklist() {
        if (!approverDocumentType) {
            setError("Please select a document type.");
            setStatus("");
            return;
        }

        // Find the checklist and invoice_type_id for the selected document type
        const checklist = checklists.find((c) => c.invoice_type_name === approverDocumentType);
        if (!checklist) {
            setError("Document type not found.");
            setStatus("");
            return;
        }

        const invoiceTypeId = checklist.id;
        const checklistItems = checklist.items || [];

        // Build approver_selections from checkbox state
        // approverSelections is keyed like: "${rowId}-approver-${approverIndex}" -> boolean
        // We need to convert it to: { approver1: ["item1", "item3"], approver2: [...], ... }
        const approverSelectionsPayload = {};
        for (let approverNum = 1; approverNum <= 6; approverNum++) {
            const selectedItems = [];
            checklistItems.forEach((item, itemIndex) => {
                const rowId = `${invoiceTypeId}-${itemIndex}`;
                const checkboxKey = `${rowId}-approver-${approverNum}`;
                if (approverSelections[checkboxKey]) {
                    selectedItems.push(item);
                }
            });
            approverSelectionsPayload[`approver${approverNum}`] = selectedItems;
        }

        setError("");
        setStatus("");

        apiRequest(`/checklists/${invoiceTypeId}/`, {
            method: "POST",
            body: {
                approver_selections: approverSelectionsPayload,
            },
        })
            .then((response) => {
                setStatus(response?.message || `Approver checklist saved for ${approverDocumentType}.`);
                loadApproverSelectionsForType(approverDocumentType);
            })
            .catch((requestError) => {
                setError(requestError.message || "Unable to save approver checklist.");
            });
    }

    return (
        <section className="teams-view teams-view--checklists">
            <div className="workspace-panel teams-view__hero">
                <div>
                    {/* <p className="eyebrow">Invoice Approval</p> */}
                    <h2>Invoice Approval Checklists</h2>

                    {/* <p className="config-view__intro">
                        Configure checklist items by document type and map approver-level tick or untick selections.
                    </p> */}
                </div>
                <label className="config-field checklist-enabled-control">
                    <span>Checklist Enabled</span>
                    <select
                        value={invoiceConfiguration.checklistEnabled}
                        onChange={(event) => handleChecklistEnabledChange(event.target.value)}
                        disabled={loadingChecklistConfiguration || savingChecklistConfiguration}
                    >
                        <option value="Y">Yes</option>
                        <option value="N">No</option>
                    </select>
                </label>
                <div className="teams-view__summary">
                    <span className="teams-view__summary-count">{checklists.length}</span>
                    <span className="teams-view__summary-label">document types with checklists</span>
                </div>
            </div>

            <div className="workspace-panel checklist-tabs" role="tablist" aria-label="Checklist options">
                <button
                    type="button"
                    className={`ghost-button checklist-tabs__button${activeTab === "add_checklist" ? " is-active" : ""}`}
                    onClick={() => setActiveTab("add_checklist")}
                >
                    Add Checklist
                </button>
                <button
                    type="button"
                    className={`ghost-button checklist-tabs__button${activeTab === "approver_checklist" ? " is-active" : ""}`}
                    onClick={() => setActiveTab("approver_checklist")}
                >
                    Approvers Checklist
                </button>
            </div>

            {invoiceTypeError ? <div className="config-banner config-banner--error">{invoiceTypeError}</div> : null}
            {status ? <div className="config-banner config-banner--success">{status}</div> : null}
            {error ? <div className="config-banner config-banner--error">{error}</div> : null}

            {activeTab === "add_checklist" ? (
                <div className="teams-layout">
                    <form
                        className={`workspace-panel teams-form${checklistsEnabled ? "" : " checklist-creation--disabled"}`}
                        onSubmit={handleSubmitChecklist}
                    >
                        <div className="users-form__header">
                            <div>
                                <p className="eyebrow">Add Checklist</p>
                                <h2>Create checklist by document type</h2>
                            </div>
                        </div>

                        <label className="config-field">
                            <span>Select Document Type</span>
                            <select
                                value={selectedDocumentType}
                                onChange={(event) => setSelectedDocumentType(event.target.value)}
                                disabled={!checklistsEnabled || loadingInvoiceTypes}
                                required
                            >
                                <option value="">Select document type</option>
                                {invoiceTypes.map((documentType) => (
                                    <option key={documentType} value={documentType}>
                                        {documentType}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className="config-field">
                            <span>Checklist Items (comma-separated)</span>
                            <textarea
                                value={checklistItemsRaw}
                                onChange={(event) => setChecklistItemsRaw(event.target.value)}
                                placeholder="Invoice Number Match, GST Check, Amount Validation"
                                rows={4}
                                disabled={!checklistsEnabled}
                                required
                            />
                        </label>

                        <button
                            className="primary-button"
                            type="submit"
                            disabled={!checklistsEnabled || savingChecklist || loadingInvoiceTypes}
                        >
                            {savingChecklist ? "Saving..." : "Submit"}
                        </button>
                    </form>

                    <div className="workspace-panel">
                        <div className="teams-table__header">
                            <div>
                                <p className="eyebrow">Added Checklists</p>
                                <h2>Checklist Directory</h2>
                            </div>
                        </div>

                        <div className="config-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Document Type</th>
                                        <th>Checklist Items</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingChecklists ? (
                                        <tr>
                                            <td colSpan="2" style={{ textAlign: "center", color: "#999" }}>
                                                Loading checklists...
                                            </td>
                                        </tr>
                                    ) : !aggregatedRows.length ? (
                                        <tr>
                                            <td colSpan="2" style={{ textAlign: "center", color: "#999" }}>
                                                No checklist items added yet.
                                            </td>
                                        </tr>
                                    ) : (
                                        aggregatedRows.map((row) => (
                                            <tr key={row.id}>
                                                <td>{row.invoice_type_name}</td>
                                                <td>{row.items_joined}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="workspace-panel">
                    <div className="users-form__header">
                        <div>
                            <p className="eyebrow">Approvers Checklist</p>
                            <h2>Set approver ticks by document type</h2>
                        </div>
                    </div>

                    <label className="config-field checklist-approver-selector">
                        <span>Select Document Type</span>
                        <select
                            value={approverDocumentType}
                            onChange={(event) => {
                                const nextType = event.target.value;
                                setApproverDocumentType(nextType);
                                loadApproverSelectionsForType(nextType);
                            }}
                        >
                            <option value="">Select document type</option>
                            {invoiceTypes.map((documentType) => (
                                <option key={documentType} value={documentType}>
                                    {documentType}
                                </option>
                            ))}
                        </select>
                    </label>

                    {approverDocumentType ? (
                        <>
                            <div className="config-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Checklist Item</th>
                                            <th>Approver 1</th>
                                            <th>Approver 2</th>
                                            <th>Approver 3</th>
                                            <th>Approver 4</th>
                                            <th>Approver 5</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!approverRows.length ? (
                                            <tr>
                                                <td colSpan="6" style={{ textAlign: "center", color: "#999" }}>
                                                    No checklist items found for this document type.
                                                </td>
                                            </tr>
                                        ) : (
                                            approverRows.map((row) => (
                                                <tr key={row.id}>
                                                    <td>{row.checklistItem}</td>
                                                    {[1, 2, 3, 4, 5].map((approverIndex) => (
                                                        <td key={`${row.id}-${approverIndex}`} style={{ textAlign: "center" }}>
                                                            <input
                                                                type="checkbox"
                                                                className="checklist-approver-checkbox"
                                                                checked={getApproverSelectionValue(row.id, approverIndex)}
                                                                onChange={() => toggleApproverSelection(row.id, approverIndex)}
                                                                aria-label={`Approver ${approverIndex} for ${row.checklistItem}`}
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="checklist-approver-actions">
                                <button
                                    type="button"
                                    className="primary-button"
                                    onClick={handleSubmitApproverChecklist}
                                >
                                    Submit
                                </button>
                            </div>
                        </>
                    ) : (
                        <p className="config-note">Select a document type to view checklist rows and approver options.</p>
                    )}
                </div>
            )}
        </section>
    );
}
